import { useMemo } from 'react';
import { Modal } from '@mantine/core';
import { invoiceTypeEnum } from '@billing/validation/invoice';
import { useGetClaim, useUpdateClaim } from 'api/resource';
import {
  Claim,
  Payor,
  PayorEnum,
  PayorType,
  Provider,
  Status,
} from 'interfaces';
import { ClaimDebits } from './ClaimDebits';
import { ClaimHistory } from './ClaimHistory';
import { ClaimSubmissions } from './ClaimSubmissions';
import { ClaimInvoices } from './ClaimInvoices';
import { ClaimQueryModal } from 'pages/claims/components';
import { ClaimInvoiceModal } from './ClaimToolBarComponents/ClaimInvoiceModal';
import { ClaimActionConfirmModal } from './ClaimActionConfirmModal';
import { ClaimActionConfirmDialogModal } from './ClaimActionConfirmDialogModal';
import { ConfirmWithCommentModal } from './ConfirmWithCommentModal';
import { InternalCommentModal } from './InternalCommentModal';
import { MarkAsPaidModal } from './MarkAsPaidModal';
import { useRecordClaimPayment } from 'api/resource/claimPayment';
import type { RecordClaimPaymentInput } from '@billing/validation/claim';
import { ViewReceipts } from './ViewReceipts';
import { MatchClaimModal } from './ModalComponents/MatchClaimModal';
import { ClaimActionRequestModal } from './ModalComponents/ClaimActionsManagement/ClaimActionRequestModal';
import { ViewClaimActionsModal } from './ModalComponents/ClaimActionsManagement/ViewClaimActionsModal';
import { ClaimSourceDataModal } from './ClaimSourceDataModal';
import { ViewClaimAttachmentsModal } from './ViewClaimAttachmentsModal';
import { SupportingDocumentUploadModal } from './SupportingDocumentUploadModal';
import { isFeatureEnabled } from 'firebase';
import { ClaimBatchSummaryContent } from './ClaimBatchSummaryContent';
import { getClaimPaymentRequestedAmount } from '../utils/claimTableRows';
import { CLAIM_TARIFF_STATUS } from '@billing/validation/tariff';
import {
  hasPaidTariffDebits,
  PaidTariffPaymentSummary,
} from './PaidTariffPaymentSummary';
import { useNotification, useTranslation } from '@refinedev/core';

interface ClaimRowModalProps {
  status: Status[];
  providers: Provider[];
  payors: Payor[];
  selectedClaim: Claim | null;
  setSelectedClaim: (claim: Claim | null) => void;
  historyOpen: boolean;
  setHistoryOpen: (open: boolean) => void;
  debitOpen: boolean;
  setDebitOpen: (open: boolean) => void;
  submissionOpen: boolean;
  setSubmissionOpen: (open: boolean) => void;
  viewBatchOpen: boolean;
  setViewBatchOpen: (open: boolean) => void;
  cancelClaimOpen: boolean;
  setCancelClaimOpen: (open: boolean) => void;
  invoiceOpen: boolean;
  setInvoiceOpen: (open: boolean) => void;
  queryModalOpen: boolean;
  setQueryModalOpen: (open: boolean) => void;
  invoiceModalOpen: boolean;
  setInvoiceModalOpen: (open: boolean) => void;
  resubmitOpen: boolean;
  setResubmitOpen: (open: boolean) => void;
  reconcileOpen: boolean;
  setReconcileOpen: (open: boolean) => void;
  matchOpen: boolean;
  setMatchOpen: (open: boolean) => void;
  writeOffOpen: boolean;
  setWriteOffOpen: (open: boolean) => void;
  markReadyOpen: boolean;
  setMarkReadyOpen: (open: boolean) => void;
  markPaidOpen: boolean;
  setMarkPaidOpen: (open: boolean) => void;
  markIncompleteOpen: boolean;
  setMarkIncompleteOpen: (open: boolean) => void;
  markHistoricalOpen: boolean;
  setMarkHistoricalOpen: (open: boolean) => void;
  markUninsuredOpen: boolean;
  setMarkUninsuredOpen: (open: boolean) => void;
  markPendingWriteOffOpen: boolean;
  setMarkPendingWriteOffOpen: (open: boolean) => void;
  internalCommentOpen: boolean;
  setInternalCommentOpen: (open: boolean) => void;
  receiptOpen: boolean;
  setReceiptOpen: (open: boolean) => void;
  handleReconcileSelected: () => void;
  actionRequestOpen: boolean;
  setActionRequestOpen: (open: boolean) => void;
  viewActionsOpen: boolean;
  setViewActionsOpen: (open: boolean) => void;
  sourceDataOpen: boolean;
  setSourceDataOpen: (open: boolean) => void;
  attachmentsOpen: boolean;
  setAttachmentsOpen: (open: boolean) => void;
  supportingDocumentOpen: boolean;
  setSupportingDocumentOpen: (open: boolean) => void;
}

export const ClaimRowModals = ({
  selectedClaim,
  setSelectedClaim,
  status,
  providers,
  payors,
  historyOpen,
  setHistoryOpen,
  debitOpen,
  setDebitOpen,
  submissionOpen,
  setSubmissionOpen,
  viewBatchOpen,
  setViewBatchOpen,
  cancelClaimOpen,
  setCancelClaimOpen,
  invoiceOpen,
  setInvoiceOpen,
  queryModalOpen,
  setQueryModalOpen,
  invoiceModalOpen,
  setInvoiceModalOpen,
  resubmitOpen,
  setResubmitOpen,
  reconcileOpen,
  setReconcileOpen,
  matchOpen,
  setMatchOpen,
  writeOffOpen,
  setWriteOffOpen,
  markReadyOpen,
  setMarkReadyOpen,
  markPaidOpen,
  setMarkPaidOpen,
  markIncompleteOpen,
  setMarkIncompleteOpen,
  markHistoricalOpen,
  setMarkHistoricalOpen,
  markUninsuredOpen,
  setMarkUninsuredOpen,
  markPendingWriteOffOpen,
  setMarkPendingWriteOffOpen,
  internalCommentOpen,
  setInternalCommentOpen,
  receiptOpen,
  setReceiptOpen,
  handleReconcileSelected,
  actionRequestOpen,
  setActionRequestOpen,
  viewActionsOpen,
  setViewActionsOpen,
  sourceDataOpen,
  setSourceDataOpen,
  attachmentsOpen,
  setAttachmentsOpen,
  supportingDocumentOpen,
  setSupportingDocumentOpen,
}: ClaimRowModalProps) => {
  const isActionManagementEnabled = isFeatureEnabled('action_management');
  const updateClaimQuery = useUpdateClaim();
  const recordClaimPayment = useRecordClaimPayment();
  const { open: openNotification } = useNotification();
  const { translate } = useTranslation();
  const resubmittableStatuses = new Set<string>([
    CLAIM_TARIFF_STATUS.REJECTED,
    CLAIM_TARIFF_STATUS.UNDERPAID,
    CLAIM_TARIFF_STATUS.OVERPAID,
  ]);
  const resubmittableTariffIDs = (selectedClaim?.tariffs ?? [])
    .filter((tariff) => resubmittableStatuses.has(tariff.status?.trim() ?? ''))
    .map((tariff) => tariff.id);
  const claimHasPaidDebits = hasPaidTariffDebits(selectedClaim?.debits);
  const markPaidTariffs = useMemo(() => {
    if (!selectedClaim) return [];

    return (selectedClaim.tariffs ?? []).map((tariff) => ({
      id: tariff.id,
      code: tariff.code,
      requestedAmount: getClaimPaymentRequestedAmount(selectedClaim, tariff),
    }));
  }, [selectedClaim]);
  const requiresPaymentAmounts =
    selectedClaim?.payor?.type !== PayorType.PUBLIC ||
    selectedClaim?.payor?.name === PayorEnum.wcb ||
    selectedClaim.status === CLAIM_TARIFF_STATUS.PAYOR_INVOICED ||
    selectedClaim.invoices?.[0]?.type === invoiceTypeEnum.tariff;
  const resubmissionHasPaidDebits = hasPaidTariffDebits(
    selectedClaim?.debits,
    resubmittableTariffIDs
  );
  const { refetch: refetchSelectedClaim } = useGetClaim({
    id: selectedClaim?.id || '',
    displayNotification: false,
  });
  const refreshSelectedClaim = async () => {
    if (!selectedClaim?.id) return;
    const refreshedClaim = (await refetchSelectedClaim()).data;
    if (refreshedClaim) {
      setSelectedClaim(refreshedClaim);
    }
  };

  const applyTariffStatusTransition = async ({
    targetStatus,
    onClose,
    comment,
    isEligible = () => true,
  }: {
    targetStatus: string;
    onClose: () => void;
    comment?: string;
    isEligible?: (status: string) => boolean;
  }) => {
    if (!selectedClaim?.id) {
      onClose();
      return;
    }

    const nextTariffs = (selectedClaim.tariffs || []).map((tariff) => {
      const tariffStatus = tariff.status?.trim() ?? '';
      if (!isEligible(tariffStatus)) return tariff;
      return {
        ...tariff,
        status: targetStatus,
      };
    });

    const hasTariffChanges = nextTariffs.some(
      (tariff, index) => tariff.status !== selectedClaim.tariffs[index]?.status
    );

    const hasCommentUpdate =
      typeof comment === 'string' && comment.trim().length > 0;

    if (!hasTariffChanges && !hasCommentUpdate) {
      onClose();
      return;
    }

    const updatedClaim = {
      ...selectedClaim,
      tariffs: nextTariffs,
      internalComments: hasCommentUpdate ? comment : '',
    } as Claim;

    const updateData = {
      dirtyFields: {
        tariffs: hasTariffChanges,
        internalComments: hasCommentUpdate,
      },
      newData: { ...updatedClaim },
    };
    await updateClaimQuery.mutateAsync(updateData);
    onClose();
  };

  const handleConfirmCancel = async () => {
    await applyTariffStatusTransition({
      targetStatus: CLAIM_TARIFF_STATUS.CANCELLED,
      onClose: () => setCancelClaimOpen(false),
      isEligible: (status) => status !== CLAIM_TARIFF_STATUS.CANCELLED,
    });
  };

  const handleConfirmInternalComment = async (comment: string) => {
    const updatedClaim = {
      ...selectedClaim,
      internalComments: comment,
    } as Claim;
    const updateData = {
      dirtyFields: { internalComments: true },
      newData: { ...updatedClaim },
    };
    await updateClaimQuery.mutateAsync(updateData);
    setInternalCommentOpen(false);
  };

  const handleConfirmMarkPaid = async (input: RecordClaimPaymentInput) => {
    if (!selectedClaim?.id) {
      setMarkPaidOpen(false);
      return;
    }

    const result = await recordClaimPayment.mutateAsync({
      ...input,
      claimID: selectedClaim.id,
    });
    if (result.warnings.length) {
      openNotification?.({
        type: 'progress',
        message: 'Payment recorded with a warning',
        description: result.warnings.join('\n'),
      });
    }
    setMarkPaidOpen(false);
    setSelectedClaim(null);
  };

  const handleConfirmAcceptExistingPayment = async (comment: string) => {
    await applyTariffStatusTransition({
      targetStatus: CLAIM_TARIFF_STATUS.PAID,
      comment,
      onClose: () => setMarkPaidOpen(false),
    });
    setSelectedClaim(null);
  };

  const handleConfirmMarkReady = async () => {
    await applyTariffStatusTransition({
      targetStatus: CLAIM_TARIFF_STATUS.READY,
      onClose: () => setMarkReadyOpen(false),
    });
    setSelectedClaim(null);
  };

  const handleConfirmMarkIncomplete = async () => {
    await applyTariffStatusTransition({
      targetStatus: CLAIM_TARIFF_STATUS.INCOMPLETE,
      onClose: () => setMarkIncompleteOpen(false),
    });
    setSelectedClaim(null);
  };

  const handleConfirmMarkUninsured = async (comment: string) => {
    await applyTariffStatusTransition({
      targetStatus: CLAIM_TARIFF_STATUS.UNINSURED,
      comment,
      onClose: () => setMarkUninsuredOpen(false),
      isEligible: (status) =>
        status !== CLAIM_TARIFF_STATUS.CANCELLED &&
        status !== CLAIM_TARIFF_STATUS.WRITTEN_OFF,
    });
    setSelectedClaim(null);
  };

  const handleConfirmMarkPendingWriteOff = async (comment: string) => {
    await applyTariffStatusTransition({
      targetStatus: CLAIM_TARIFF_STATUS.PENDING_WRITE_OFF,
      comment,
      onClose: () => setMarkPendingWriteOffOpen(false),
      isEligible: (status) =>
        status !== CLAIM_TARIFF_STATUS.CANCELLED &&
        status !== CLAIM_TARIFF_STATUS.WRITTEN_OFF,
    });
    setSelectedClaim(null);
  };
  const handleConfirmResubmit = async () => {
    await applyTariffStatusTransition({
      targetStatus: CLAIM_TARIFF_STATUS.READY,
      onClose: () => setResubmitOpen(false),
      isEligible: (status) =>
        status === CLAIM_TARIFF_STATUS.REJECTED ||
        status === CLAIM_TARIFF_STATUS.UNDERPAID ||
        status === CLAIM_TARIFF_STATUS.OVERPAID,
    });
    setSelectedClaim(null);
  };
  const handleConfirmReconcile = async (comment: string) => {
    await applyTariffStatusTransition({
      targetStatus: CLAIM_TARIFF_STATUS.PAID,
      comment,
      onClose: () => setReconcileOpen(false),
      isEligible: (status) =>
        status === CLAIM_TARIFF_STATUS.UNDERPAID ||
        status === CLAIM_TARIFF_STATUS.OVERPAID,
    });
    handleReconcileSelected();
    setReconcileOpen(false);
    setSelectedClaim(null);
  };
  const handleConfirmWriteOff = async (comment: string) => {
    await applyTariffStatusTransition({
      targetStatus: CLAIM_TARIFF_STATUS.WRITTEN_OFF,
      comment,
      onClose: () => setWriteOffOpen(false),
      isEligible: (status) => status === CLAIM_TARIFF_STATUS.PENDING_WRITE_OFF,
    });
    setSelectedClaim(null);
  };

  // Mark claim as historical by updating its status, then close the modal.
  const handleConfirmMarkHistorical = async () => {
    await applyTariffStatusTransition({
      targetStatus: CLAIM_TARIFF_STATUS.HISTORICAL,
      onClose: () => setMarkHistoricalOpen(false),
    });
    setSelectedClaim(null);
  };
  return (
    <>
      <ClaimInvoiceModal
        opened={invoiceModalOpen}
        close={() => {
          setInvoiceModalOpen(false);
          setSelectedClaim(null);
        }}
        selectedClaim={selectedClaim}
        setSelectedClaim={setSelectedClaim}
        payors={payors}
      />
      <ClaimQueryModal
        opened={queryModalOpen}
        close={() => {
          setQueryModalOpen(false);
          setSelectedClaim(null);
        }}
        selectedClaim={selectedClaim}
      />
      <Modal
        opened={historyOpen}
        onClose={() => {
          setHistoryOpen(false);
          setSelectedClaim(null);
        }}
        size="xl"
        closeOnEscape={false}
        centered
      >
        <ClaimHistory
          claimID={selectedClaim?.id}
          claim={selectedClaim}
          status={status}
          providers={providers}
        />
      </Modal>
      <Modal
        opened={debitOpen}
        onClose={() => {
          setDebitOpen(false);
          setSelectedClaim(null);
        }}
        closeOnEscape={false}
        size="xl"
        centered
        title={translate(
          'pages:claims.paymentHistory.title',
          'Payment History'
        )}
        styles={{
          title: {
            fontSize: 'var(--mantine-h2-font-size)',
            fontWeight: 'var(--mantine-h2-font-weight)',
            lineHeight: 'var(--mantine-h2-line-height)',
          },
        }}
      >
        <ClaimDebits claimID={selectedClaim?.id} />
      </Modal>
      <Modal
        opened={submissionOpen}
        onClose={() => {
          setSubmissionOpen(false);
          setSelectedClaim(null);
        }}
        closeOnEscape={false}
        size="lg"
        centered
      >
        <ClaimSubmissions claimID={selectedClaim?.id} />
      </Modal>
      <Modal
        opened={viewBatchOpen}
        onClose={() => {
          setViewBatchOpen(false);
          setSelectedClaim(null);
        }}
        closeOnEscape={false}
        size="xl"
        centered
        title="View Batch"
      >
        {selectedClaim?.batchID ? (
          <ClaimBatchSummaryContent
            siteID={selectedClaim.siteID}
            batchID={selectedClaim.batchID}
            enabled={viewBatchOpen && !!selectedClaim?.batchID}
          />
        ) : null}
      </Modal>
      <Modal
        opened={receiptOpen}
        onClose={() => {
          setReceiptOpen(false);
          setSelectedClaim(null);
        }}
        closeOnEscape={false}
        size="lg"
        centered
      >
        <ViewReceipts invoiceID={selectedClaim?.invoices?.[0]?.id || ''} />
      </Modal>
      <ClaimActionConfirmDialogModal
        open={cancelClaimOpen}
        setOpen={setCancelClaimOpen}
        onConfirm={handleConfirmCancel}
        title="Cancel Claim"
        body="Are you sure you want to cancel this claim?"
        emphasizePhrase="cancel"
        cancelText="Back to Safety"
        confirmText="Delete Claim"
        confirmButtonColor="red-dark"
      />
      <ClaimActionConfirmModal
        open={markReadyOpen}
        setOpen={setMarkReadyOpen}
        onConfirm={handleConfirmMarkReady}
        title="Mark Ready"
        body={
          claimHasPaidDebits
            ? 'Are you sure you want to resubmit these tariffs with payments?'
            : 'Are you sure you want to mark this claim as Ready?'
        }
        emphasizePhrase="Ready"
      >
        {claimHasPaidDebits ? (
          <PaidTariffPaymentSummary
            tariffs={selectedClaim?.tariffs ?? []}
            debits={selectedClaim?.debits ?? []}
            timezone={selectedClaim?.site?.timezone}
            showWarning={false}
          />
        ) : null}
      </ClaimActionConfirmModal>
      <ClaimActionConfirmModal
        open={markIncompleteOpen}
        setOpen={setMarkIncompleteOpen}
        onConfirm={handleConfirmMarkIncomplete}
        title="Mark Incomplete"
        body="Are you sure you want to mark this claim as Incomplete?"
        emphasizePhrase="Incomplete"
      />
      {requiresPaymentAmounts ? (
        <MarkAsPaidModal
          open={markPaidOpen}
          setOpen={setMarkPaidOpen}
          onConfirm={handleConfirmMarkPaid}
          title="Mark Paid"
          body="Are you sure you want to mark this claim as Paid?"
          emphasizePhrase="Paid"
          tariffs={markPaidTariffs}
          invoice={selectedClaim?.invoices?.[0] || null}
          claimID={selectedClaim?.id || ''}
        />
      ) : (
        <ConfirmWithCommentModal
          open={markPaidOpen}
          setOpen={setMarkPaidOpen}
          onConfirm={handleConfirmAcceptExistingPayment}
          title="Mark Paid"
          body="Are you sure you want to mark this claim as Paid?"
          emphasizePhrase="Paid"
        />
      )}
      <ClaimActionConfirmModal
        open={markHistoricalOpen}
        setOpen={setMarkHistoricalOpen}
        onConfirm={handleConfirmMarkHistorical}
        title="Mark Historical"
        body="Are you sure you want to mark this claim as Historical?"
        emphasizePhrase="Historical"
      />
      <ConfirmWithCommentModal
        open={markUninsuredOpen}
        setOpen={setMarkUninsuredOpen}
        onConfirm={handleConfirmMarkUninsured}
        title="Mark Uninsured"
        body="Are you sure you want to mark this claim as Uninsured?"
        emphasizePhrase="Uninsured"
      />
      <ConfirmWithCommentModal
        open={markPendingWriteOffOpen}
        setOpen={setMarkPendingWriteOffOpen}
        onConfirm={handleConfirmMarkPendingWriteOff}
        title="Mark Pending Write-Off"
        body="Are you sure you want to mark this claim as Pending Write-Off?"
        emphasizePhrase="Pending Write-Off"
      />
      <ClaimActionConfirmModal
        open={resubmitOpen}
        setOpen={setResubmitOpen}
        onConfirm={handleConfirmResubmit}
        title="Mark For Resubmission"
        body={
          resubmissionHasPaidDebits
            ? 'Are you sure you want to resubmit these tariffs with payments?'
            : 'Are you sure you want to mark this claim for Resubmission?'
        }
        emphasizePhrase="Resubmission"
      >
        {resubmissionHasPaidDebits ? (
          <PaidTariffPaymentSummary
            tariffs={selectedClaim?.tariffs ?? []}
            debits={selectedClaim?.debits ?? []}
            tariffIDs={resubmittableTariffIDs}
            timezone={selectedClaim?.site?.timezone}
            showWarning={false}
          />
        ) : null}
      </ClaimActionConfirmModal>
      <ConfirmWithCommentModal
        open={reconcileOpen}
        setOpen={setReconcileOpen}
        onConfirm={handleConfirmReconcile}
        title="Reconcile Claim"
        body="Are you sure you want to reconcile all tariffs in this claim?"
        emphasizePhrase="reconcile"
      />
      <MatchClaimModal
        open={matchOpen}
        setOpen={setMatchOpen}
        title="Match Claim"
        selectedClaim={selectedClaim}
      />
      <ConfirmWithCommentModal
        open={writeOffOpen}
        setOpen={setWriteOffOpen}
        onConfirm={handleConfirmWriteOff}
        title="Write-Off Claim"
        body="Are you sure you want to write-off this claim?"
        emphasizePhrase="write-off"
      />
      <InternalCommentModal
        open={internalCommentOpen}
        setOpen={setInternalCommentOpen}
        onConfirm={handleConfirmInternalComment}
      />
      <Modal
        opened={invoiceOpen}
        onClose={() => {
          setInvoiceOpen(false);
          setSelectedClaim(null);
        }}
        closeOnEscape={false}
        size="lg"
        centered
      >
        <ClaimInvoices
          invoices={selectedClaim?.invoices}
          onInvoicesChanged={refreshSelectedClaim}
          onFaxSentSuccess={() => {
            setInvoiceOpen(false);
            setSelectedClaim(null);
          }}
          payors={payors}
        />
      </Modal>
      {isActionManagementEnabled && (
        <ClaimActionRequestModal
          open={actionRequestOpen}
          setOpen={setActionRequestOpen}
          claimID={selectedClaim?.id || ''}
          providerID={selectedClaim?.providerID || ''}
        />
      )}
      {isActionManagementEnabled && (
        <ViewClaimActionsModal
          open={viewActionsOpen}
          setOpen={setViewActionsOpen}
          claim={selectedClaim}
        />
      )}
      <ClaimSourceDataModal
        open={sourceDataOpen}
        setOpen={(open) => {
          setSourceDataOpen(open);
          if (!open) {
            setSelectedClaim(null);
          }
        }}
        claim={selectedClaim}
      />
      <ViewClaimAttachmentsModal
        open={attachmentsOpen}
        setOpen={(open) => {
          setAttachmentsOpen(open);
          if (!open) setSelectedClaim(null);
        }}
        claim={selectedClaim}
      />
      <SupportingDocumentUploadModal
        open={supportingDocumentOpen}
        setOpen={(open) => {
          setSupportingDocumentOpen(open);
          if (!open) setSelectedClaim(null);
        }}
        claim={selectedClaim}
      />
    </>
  );
};
